import React from 'react';
import Icon from './Icon';

const TopCut = ({ tournament, onUpdate, onBack, onFinishTournament }) => {
  // Créer la structure du Top Cut si elle n'existe pas
  if (!tournament.topCut) {
    const topPlayers = tournament.players
      .filter(p => !p.dropped)
      .slice(0, tournament.topCutSize);

    const brackets = generateBrackets(topPlayers, tournament.topCutSize);
    
    const updatedTournament = {
      ...tournament,
      topCut: {
        brackets,
        currentRound: 'semifinals',
        completed: false
      }
    };
    
    onUpdate(updatedTournament);
    return null;
  }

  const { brackets } = tournament.topCut;

  const updateMatchResult = (matchId, winnerId) => {
    const updatedBrackets = { ...brackets };
    
    // Trouver et mettre à jour le match
    Object.keys(updatedBrackets).forEach(round => {
      updatedBrackets[round] = updatedBrackets[round].map(match => {
        if (match.id === matchId) {
          return { ...match, winnerId, completed: true };
        }
        return match;
      });
    });

    // Propager les gagnants vers le round suivant
    propagateWinners(updatedBrackets);

    const updatedTournament = {
      ...tournament,
      topCut: {
        ...tournament.topCut,
        brackets: updatedBrackets
      }
    };

    onUpdate(updatedTournament);
  };

  const getPlayerName = (playerId) => {
    return tournament.players.find(p => p.id === playerId)?.name || 'TBD';
  };

  const getRoundName = (round) => {
    const names = {
      quarterfinals: 'Quarts de finale',
      semifinals: 'Demi-finales', 
      final: 'Finale'
    };
    return names[round] || round;
  };

  const isRoundComplete = (round) => {
    return brackets[round]?.every(match => match.completed) || false;
  };

  const isTournamentComplete = () => {
    return brackets.final?.[0]?.completed || false;
  };

  return (
    <div className="min-h-screen p-4">
      <button onClick={onBack} className="mb-4 text-red-500 flex items-center gap-2">
        <Icon d="M15 19l-7-7 7-7" />
        Retour
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Top {tournament.topCutSize}</h1>
        <p className="text-gray-400">Phase d'élimination directe</p>
      </div>

      {/* Interface Top Cut */}
      <div className="space-y-6">
        {Object.entries(brackets).map(([round, matches]) => (
          <div key={round} className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{getRoundName(round)}</h2>
              {isRoundComplete(round) && (
                <span className="text-green-400 text-sm font-semibold">✓ Terminé</span>
              )}
            </div>
            
            <div className="space-y-4">
              {matches.map(match => (
                <MatchBox
                  key={match.id}
                  match={match}
                  getPlayerName={getPlayerName}
                  updateMatchResult={updateMatchResult}
                  title={`Match ${matches.indexOf(match) + 1}`}
                  isHighlight={round === 'final'}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {isTournamentComplete() && (
        <div className="mt-6 bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">🏆 Tournoi Terminé !</h2>
          <p className="text-yellow-100 mb-4">
            Champion : {getPlayerName(brackets.final[0].winnerId)}
          </p>
          <button
            onClick={onFinishTournament}
            className="bg-white text-yellow-600 px-6 py-3 rounded-lg font-bold hover:bg-yellow-50 transition"
          >
            Terminer le tournoi
          </button>
        </div>
      )}
    </div>
  );
};

// Composant pour une boîte de match
const MatchBox = ({ match, getPlayerName, updateMatchResult, title, isHighlight = false }) => {
  const canPlay = match.player1Id && match.player2Id && !match.completed;
  
  const handlePlayerClick = (playerId) => {
    if (!canPlay) return;
    
    const playerName = getPlayerName(playerId);
    const isConfirmed = window.confirm(`Confirmer la victoire de ${playerName} ?`);
    
    if (isConfirmed) {
      updateMatchResult(match.id, playerId);
    }
  };
  
  return (
    <div className={`bg-gray-800 rounded-xl p-4 min-w-[240px] shadow-lg ${isHighlight ? 'ring-2 ring-yellow-500' : ''}`}>
      <div className="text-center text-sm font-semibold text-gray-300 mb-3">{title}</div>
      
      <div className="space-y-2">
        <div 
          className={`p-3 rounded-lg flex items-center justify-between transition-all duration-200 ${
            match.winnerId === match.player1Id 
              ? 'bg-green-600 text-white shadow-md' 
              : canPlay 
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 cursor-pointer transform hover:scale-105' 
                : 'bg-gray-700 text-gray-200'
          }`}
          onClick={() => handlePlayerClick(match.player1Id)}
        >
          <span className="font-semibold text-base">{getPlayerName(match.player1Id)}</span>
          {match.winnerId === match.player1Id && <span className="text-xl">🏆</span>}
        </div>
        
        <div className="text-center text-gray-400 font-bold text-sm py-1">VS</div>
        
        <div 
          className={`p-3 rounded-lg flex items-center justify-between transition-all duration-200 ${
            match.winnerId === match.player2Id 
              ? 'bg-green-600 text-white shadow-md' 
              : canPlay 
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 cursor-pointer transform hover:scale-105' 
                : 'bg-gray-700 text-gray-200'
          }`}
          onClick={() => handlePlayerClick(match.player2Id)}
        >
          <span className="font-semibold text-base">{getPlayerName(match.player2Id)}</span>
          {match.winnerId === match.player2Id && <span className="text-xl">🏆</span>}
        </div>
      </div>

      {match.completed && (
        <div className="text-center text-green-400 text-sm mt-3 font-medium">
          Gagnant : {getPlayerName(match.winnerId)}
        </div>
      )}

      {!canPlay && !match.completed && (
        <div className="text-center text-gray-400 text-sm mt-3">
          En attente...
        </div>
      )}
    </div>
  );
};

// Fonctions utilitaires
const generateBrackets = (players, topCutSize) => {
  const brackets = {};

  if (topCutSize === 4) {
    // Demi-finales : 1er vs 4ème, 2ème vs 3ème
    brackets.semifinals = [
      {
        id: 'SF1',
        player1Id: players[0]?.id,
        player2Id: players[3]?.id,
        winnerId: null,
        completed: false
      },
      {
        id: 'SF2',
        player1Id: players[1]?.id,
        player2Id: players[2]?.id,
        winnerId: null,
        completed: false
      }
    ];

    brackets.final = [
      {
        id: 'F1',
        player1Id: null, // Gagnant SF1
        player2Id: null, // Gagnant SF2
        winnerId: null,
        completed: false
      }
    ];
  } else if (topCutSize === 8) {
    // Quarts de finale
    brackets.quarterfinals = [
      { id: 'QF1', player1Id: players[0]?.id, player2Id: players[7]?.id, winnerId: null, completed: false },
      { id: 'QF2', player1Id: players[1]?.id, player2Id: players[6]?.id, winnerId: null, completed: false },
      { id: 'QF3', player1Id: players[2]?.id, player2Id: players[5]?.id, winnerId: null, completed: false },
      { id: 'QF4', player1Id: players[3]?.id, player2Id: players[4]?.id, winnerId: null, completed: false }
    ];

    brackets.semifinals = [
      { id: 'SF1', player1Id: null, player2Id: null, winnerId: null, completed: false },
      { id: 'SF2', player1Id: null, player2Id: null, winnerId: null, completed: false }
    ];

    brackets.final = [
      { id: 'F1', player1Id: null, player2Id: null, winnerId: null, completed: false }
    ];
  }

  return brackets;
};

const propagateWinners = (brackets) => {
  // Pour Top 4
  if (brackets.semifinals && brackets.final) {
    const sf1Winner = brackets.semifinals[0]?.winnerId;
    const sf2Winner = brackets.semifinals[1]?.winnerId;
    
    if (sf1Winner && sf2Winner) {
      brackets.final[0].player1Id = sf1Winner;
      brackets.final[0].player2Id = sf2Winner;
    }
  }

  // Pour Top 8
  if (brackets.quarterfinals && brackets.semifinals) {
    const qfWinners = brackets.quarterfinals.map(match => match.winnerId).filter(Boolean);
    
    if (qfWinners.length >= 2) {
      brackets.semifinals[0].player1Id = qfWinners[0];
      brackets.semifinals[0].player2Id = qfWinners[1];
    }
    if (qfWinners.length >= 4) {
      brackets.semifinals[1].player1Id = qfWinners[2];
      brackets.semifinals[1].player2Id = qfWinners[3];
    }
  }
};

export default TopCut;